import {
  Box,
  BoxProps,
  Flex,
  FlexProps,
  Link,
  List,
  VStack,
} from '@chakra-ui/react'
import React from 'react'
import { dimensions, spacing } from '~/constants/theme'

import { asChakraVariable } from '@mangovoice/design-system'

const backgroundColorDefault = asChakraVariable('bg.subtle')
const backgroundColorAlert = asChakraVariable('red.subtle')
const backgroundColorHover = asChakraVariable('bg.subtle')
const backgroundColorActive = asChakraVariable('bg.muted')
const borderColorAlert = asChakraVariable('red.emphasized')
const borderColorDefault = asChakraVariable('border.default')
const subheadingSectionBackgroundColor = asChakraVariable('bg.default')
const helpListItemColor = asChakraVariable('fg.muted')
const helpLinkColor = asChakraVariable('brand.fg')
const helpLinkHoverColor = asChakraVariable('brand.solid')

export const DefaultSettingsWrapper = ({
  children,
}: React.PropsWithChildren) => {
  return (
    <Flex
      height={'full'}
      padding="16px"
      flexDir={'column'}
      gap={'1.25rem'}
      overflowY={'auto'}
      backgroundColor={backgroundColorDefault}
    >
      {children}
    </Flex>
  )
}
export const SubheadingSection = ({
  id,
  children,
}: React.PropsWithChildren<{ id?: string }>) => {
  return (
    <Flex
      id={id}
      borderRadius={'8px'}
      display={'flex'}
      flexDir={'column'}
      alignItems={'flex-start'}
      padding={'16px'}
      gap={'0.62rem'}
      border={'1px solid'}
      width="100%"
      maxWidth="600px"
      css={{ width: { base: '100%', xl: '50%' } }}
      borderColor={borderColorDefault}
      backgroundColor={subheadingSectionBackgroundColor}
    >
      {children}
    </Flex>
  )
}
interface MenuItemWrapperProps extends BoxProps {
  isAlert?: boolean
  children: React.ReactNode
}
export const MenuItemWrapper = ({
  isAlert,
  children,
  ...props
}: MenuItemWrapperProps) => (
  <Box
    width="100%"
    height={isAlert ? '59px' : 'aut'}
    padding="12px"
    borderRadius="8px"
    border="1px solid"
    backgroundColor={isAlert ? backgroundColorAlert : backgroundColorDefault}
    borderColor={isAlert ? borderColorAlert : borderColorDefault}
    display="flex"
    flexDirection="column"
    boxSizing="border-box"
    cursor="pointer"
    _hover={{
      backgroundColor: backgroundColorHover,
    }}
    _active={{
      backgroundColor: backgroundColorActive,
      borderColor: borderColorDefault,
    }}
    {...props}
  >
    {children}
  </Box>
)
interface MenuItemPrimaryContentWrapperProps extends FlexProps {
  hasAlert?: boolean
  children: React.ReactNode
}
export const MenuItemPrimaryContentWrapper = ({
  hasAlert,
  children,
  ...props
}: MenuItemPrimaryContentWrapperProps) => (
  <Flex
    display="flex"
    alignItems="center"
    gap="8px"
    alignSelf="stretch"
    justifyContent="space-between"
    marginBottom={hasAlert ? spacing.spacingSM : 0}
    {...props}
  >
    {children}
  </Flex>
)
export const MenuItemAlertContentWrapper = ({
  children,
  ...props
}: React.PropsWithChildren<FlexProps>) => (
  <Flex
    alignItems="flex-end"
    gap={spacing.spacingLG}
    alignSelf="stretch"
    marginBottom={spacing.spacingSM}
    {...props}
  >
    {children}
  </Flex>
)
interface MenuItemHeaderProps extends FlexProps {
  roundBottomCorners?: boolean
  children: React.ReactNode
}
export const MenuItemHeader = ({
  roundBottomCorners,
  children,
  ...props
}: MenuItemHeaderProps) => (
  <Flex
    width="100%"
    padding={props.padding ?? `${spacing.spacingLG} ${spacing.spacingXL}`}
    border={`1px solid`}
    borderColor={borderColorDefault}
    borderRadius={`${dimensions.borderRadiusXS} ${dimensions.borderRadiusXS} ${roundBottomCorners ? dimensions.borderRadiusXS : '0'} ${roundBottomCorners ? dimensions.borderRadiusXS : '0'}`}
    alignItems="center"
    position="relative"
    cursor="pointer"
    justifyContent={'space-between'}
    backgroundColor={backgroundColorDefault}
    _hover={{
      backgroundColor: borderColorDefault,
    }}
    {...props}
  >
    {children}
  </Flex>
)
export const MenuItemPanel = ({
  children,
  ...props
}: React.PropsWithChildren<React.ComponentProps<typeof VStack>>) => (
  <VStack
    width="100%"
    height="fit-content"
    padding={`${spacing.spacingXXXL} ${spacing.spacingLG} ${spacing.spacingLG}`}
    border={`1.5px solid ${borderColorDefault}`}
    borderRadius={`0 0 ${dimensions.borderRadiusXS} ${dimensions.borderRadiusXS}`}
    alignItems="center"
    position="relative"
    cursor="pointer"
    gap={spacing.spacingLG}
    {...props}
  >
    {children}
  </VStack>
)
export const DependentSectionPanel = ({
  children,
  ...props
}: React.PropsWithChildren<React.ComponentProps<typeof VStack>>) => (
  <VStack
    width="100%"
    height="fit-content"
    alignItems="center"
    gap={spacing.spacingLG}
    position="relative"
    cursor="default"
    {...props}
  >
    {children}
  </VStack>
)
interface ToggleWrapperProps extends BoxProps {
  isInHeader?: boolean
  children: React.ReactNode
}
export const ToggleWrapper = ({
  isInHeader,
  children,
  ...props
}: ToggleWrapperProps) => (
  <Box
    display="flex"
    width="34px"
    height="20px"
    right={isInHeader ? spacing.spacingXL : spacing.spacingMD}
    {...props}
  >
    {children}
  </Box>
)
interface DropdownWrapperProps {
  bottomMargin?: string
  children: React.ReactNode
}
export const DropdownWrapper: React.FC<DropdownWrapperProps> = ({
  children,
  bottomMargin,
}) => {
  return (
    <Flex
      width={'100%'}
      marginBottom={bottomMargin ?? '0'}
      flexDir={'column'}
      gap={'0.5rem'}
    >
      {children}
    </Flex>
  )
}
export const HelpListItem = ({ children }: React.PropsWithChildren) => {
  return (
    <List.Item marginLeft={'-2rem'} color={helpListItemColor}>
      {children}
    </List.Item>
  )
}
interface HelpLinkProps {
  href: string
  target?: string
  rel?: string
  children: React.ReactNode
  color?: string
}
export const HelpLink = ({
  href,
  target,
  rel,
  children,
  color,
}: HelpLinkProps) => {
  return (
    <Link
      href={href}
      target={target}
      rel={rel}
      color={color ?? helpLinkColor}
      _hover={{
        color: helpLinkHoverColor,
        textDecoration: 'underline',
      }}
    >
      {children}
    </Link>
  )
}
